import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useRegisterSW } from 'virtual:pwa-register/react';
import styled from 'styled-components';
import Button from './atoms/Buttons';
import useLatest from '../hooks/useLatest';

const FloatingNotice = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.85);
  color: #ffffff;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.45);
  z-index: 9999;
  max-width: min(460px, calc(100vw - 32px));
  flex-wrap: wrap;
`;

const Message = styled.span`
  font-size: 16px;
  line-height: 1.4;
  font-weight: 600;
`;

const CloseButton = styled(Button).attrs({ variant: 'outline' })`
  padding: 10px 18px;
  font-size: 14px;
  flex-shrink: 0;
`;

const UpdateButton = styled(Button)`
  padding: 10px 18px;
  font-size: 14px;
`;

// Pantallas donde recargar no interrumpe nada: listas y vistas de consulta que se vuelven a pedir al servidor. Por
// ahora la versión nueva se aplica solo con el botón "Actualizar", pero cada pestaña ya le responde al service worker
// si está en una de estas (ver src/service-worker.js), para poder activar después la actualización automática sin que
// las pestañas con esta versión la bloqueen.
const QUIET_SCREENS = [
  /^\/home(\/|$)/,
  /^\/selector(\/|$)/,
  /^\/sorteo-list(\/|$)/,
  /^\/sorteo\/[^/]+\/(participantes|machines)(\/|$)/,
  /^\/torneo-view-rank\/[^/]+$/,
];

export const isQuietScreen = (pathname) => QUIET_SCREENS.some((screen) => screen.test(pathname));

// Navegar dentro de la app no hace que el navegador busque un service worker nuevo, y los equipos pasan días sin
// recargar.
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000;

const PwaUpdatePrompt = () => {
  const { pathname } = useLocation();
  const latestQuiet = useLatest(isQuietScreen(pathname));
  const [dismissed, setDismissed] = useState(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    // Como hacía la plantilla de CRA: se registra después del load para no competir con la carga inicial.
    immediate: false,
    // La recarga la hace el efecto de controllerchange de abajo.
    onNeedReload() {},
    onRegisteredSW(swUrl, reg) {
      if (reg) setInterval(() => reg.update().catch(() => {}), UPDATE_CHECK_INTERVAL);
    },
  });

  // Cuando se activa una versión nueva, el código en memoria es el anterior: se recarga. useRegisterSW solo recarga si
  // la página ya estaba controlada al registrarse, y no lo está en la primera visita ni tras una recarga forzada.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    // La primera vez que el service worker toma la página no cambia de versión.
    let controlled = Boolean(navigator.serviceWorker.controller);
    const onControllerChange = () => {
      if (controlled) window.location.reload();
      controlled = true;
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  // El service worker nuevo puede preguntar a cada pestaña si puede recargarse antes de activarse.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const answer = (event) => {
      if (event.data && event.data.type === 'CAN_RELOAD' && event.ports[0]) {
        event.ports[0].postMessage({ quiet: latestQuiet.current });
      }
    };
    navigator.serviceWorker.addEventListener('message', answer);
    return () => navigator.serviceWorker.removeEventListener('message', answer);
  }, [latestQuiet]);

  if (!needRefresh || dismissed) {
    return null;
  }

  return (
    <FloatingNotice>
      <Message>Hay una nueva actualización disponible</Message>
      <UpdateButton type="button" onClick={() => updateServiceWorker()}>
        Actualizar
      </UpdateButton>
      <CloseButton type="button" aria-label="Cerrar" onClick={() => setDismissed(true)}>
        Cerrar
      </CloseButton>
    </FloatingNotice>
  );
};

export default PwaUpdatePrompt;
